import { apiRequest } from '@/core';

export type LoginCredentials = {
  email: string;
  password: string;
};

export type AuthTokens = {
  access_token: string;
  refresh_token: string;
};

export type AuthUser = {
  id: number;
  name: string;
  email: string;
  roles?: string[];
  type?: 'individual' | 'company' | null;
  preferred_language?: string | null;
};

export type LoginResponse = {
  user: CurrentUserResponse;
  tokens: AuthTokens;
};

export type CurrentUserResponse = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  name?: string | null;
  email: string;
  roles?: string[];
  type?: 'individual' | 'company' | null;
  preferred_language?: string | null;
};

export type SocialProvider = {
  provider: string;
  redirect_url: string;
};

export type SocialProvidersResponse = {
  message: string;
  providers: SocialProvider[];
};

export type SocialPrefill = {
  email: string;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  avatar_url: string | null;
};

export type SocialCallbackAuthenticated = {
  action: 'authenticated';
  data: {
    message: string;
    user: CurrentUserResponse;
    tokens: AuthTokens;
  };
};

export type SocialCallbackRegistrationRequired = {
  action: 'registration_required';
  data: {
    message: string;
    provider: string;
    email: string;
    social_auth_attempt_token: string;
    prefill: SocialPrefill;
  };
};

export type SocialCallbackResponse =
  | SocialCallbackAuthenticated
  | SocialCallbackRegistrationRequired;

type SocialCallbackApiResponse =
  | SocialCallbackResponse
  | { data: SocialCallbackResponse }
  | { data: { data: SocialCallbackResponse } };

export type RegisterPayload = {
  type: 'individual' | 'company';
  email: string;
  password?: string;
  password_confirmation?: string;
  preferred_language: 'en' | 'el';
  social_auth_attempt_token?: string;
  individual_data?: {
    first_name: string;
    last_name: string;
    phone_number: string;
    date_of_birth: string;
    occupation: string;
    gender: 'male' | 'female' | 'other';
    country_id: number;
  };
  company_data?: {
    company_name: string;
    contact_person_first_name: string;
    contact_person_last_name: string;
    country_id: number;
    contact_person_phone_number: string;
    company_address: string;
    website_url: string;
    is_agent: boolean;
    registration_no: string | null;
  };
};

export type RegisterResponse = {
  message: string;
  user: CurrentUserResponse;
  development_verification_details?: {
    user_id: number;
    hash: string;
  };
};

export type ForgotPasswordPayload = {
  email: string;
};

export type ResetPasswordPayload = {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
};

export const registerRequest = (payload: RegisterPayload) =>
  apiRequest<RegisterResponse>({
    endpoint: 'v1/register',
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });

export const verifyEmailRequest = (params: {
  id: string;
  hash: string;
  expires?: string;
  signature?: string;
}) =>
  apiRequest<{ message: string }>({
    endpoint: 'v1/email/verify',
    method: 'POST',
    body: {
      user_id: parseInt(params.id, 10),
      hash: params.hash,
      expires: params.expires,
      signature: params.signature,
    },
    skipAuthRefresh: true,
  });

export const resendVerificationRequest = (email: string) =>
  apiRequest<{ message: string }>({
    endpoint: 'v1/email/verify-resend',
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });

export const forgotPasswordRequest = (payload: ForgotPasswordPayload) =>
  apiRequest<{ message: string }>({
    endpoint: 'v1/password/forgot',
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });

export const resetPasswordRequest = (payload: ResetPasswordPayload) =>
  apiRequest<{ message: string }>({
    endpoint: 'v1/password/reset',
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });

export const loginRequest = (credentials: LoginCredentials) =>
  apiRequest<LoginResponse>({
    endpoint: 'v1/login',
    method: 'POST',
    body: credentials,
    skipAuthRefresh: true,
  });

export const fetchCurrentUserRequest = () =>
  apiRequest<CurrentUserResponse>({
    endpoint: 'v1/me',
  });

export const getSocialProviders = (redirectUrl: string) =>
  apiRequest<SocialProvidersResponse>({
    endpoint: 'v1/auth/social/providers',
    params: { redirect_url: redirectUrl },
    skipAuthRefresh: true,
  });

const unwrapSocialCallbackResponse = (
  response: SocialCallbackApiResponse,
): SocialCallbackResponse => {
  let current: unknown = response;

  for (let depth = 0; depth < 3; depth += 1) {
    if (!current || typeof current !== 'object') break;

    const candidate = current as Record<string, unknown>;
    if (
      candidate.action === 'authenticated' ||
      candidate.action === 'registration_required'
    ) {
      return current as SocialCallbackResponse;
    }

    current = candidate.data;
  }

  throw new Error('Invalid social authentication response.');
};

export const socialCallbackRequest = async (payload: {
  provider: string;
  code: string;
  state?: string | null;
  redirect_url: string;
}) => {
  const response = await apiRequest<SocialCallbackApiResponse>({
    endpoint: 'v1/auth/social/callback',
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });

  return unwrapSocialCallbackResponse(response);
};

export const mapCurrentUserToAuthUser = (
  user: CurrentUserResponse,
): AuthUser => ({
  id: user.id,
  name:
    user.display_name ||
    user.name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email,
  email: user.email,
  roles: user.roles,
  type: user.type ?? null,
  preferred_language: user.preferred_language ?? null,
});
